import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Col, Input, Row, RowProps, Skeleton } from 'antd';

import { ReactComponent as SearchIcon } from 'app/assets/img/search.svg';
import { ReactComponent as UserIcon } from 'app/assets/img/user_black.svg';

import { selectTranslations } from 'app/pages/App/slice/selectors';
import { StyledCheckbox } from 'app/pages/Cart/components/PaymentInfo';
import useAllocatedDebt from 'app/hooks/useAllocatedDebt';
import StyledButton from 'app/components/StyledButton';

interface PartnerProps {
  onChange: (value: Record<string, any>) => void;
  onProceedToBookTicket?: (bpAllocatedDebt: string | number) => void;
  styleSlots?: {
    container?: CSSProperties;
    title?: CSSProperties;
    optionContainer?: CSSProperties;
  };
  propsSlot?: {
    optionContainer?: RowProps;
    optionList?: CSSProperties;
  };
  alignPadding?: string;
}

const Partner: React.FC<PartnerProps> = props => {
  const {
    onChange,
    styleSlots,
    propsSlot,
    alignPadding = '10px',
    onProceedToBookTicket,
  } = props;

  const translation = useSelector(selectTranslations);
  const optionContainerRef = useRef<HTMLDivElement>(null);
  const [optionContainerOverflowY, setOptionContainerOverflowY] =
    useState<CSSProperties['overflowY']>('initial');

  const [partnerSelected, setPartnerSelected] = React.useState<Record<
    string,
    any
  > | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isGettedNewData, setIsGettedNewData] = useState(false);

  const { result: listingAllowcatedDebt, isSearching } = useAllocatedDebt({
    search: searchValue,
    onSuccess: () => {
      setIsGettedNewData(true);
    },
    onError: () => {
      setIsGettedNewData(true);
    },
  });

  const allocatedDebtOptionCached = useRef<Record<string, any>[]>();

  const allowcatedDebtOption = useMemo(() => {
    if (!isGettedNewData) return allocatedDebtOptionCached.current;
    setIsGettedNewData(false);

    const result = (listingAllowcatedDebt ?? [])?.reduce(
      (result, i) => {
        if (i?.id === partnerSelected?.id) return result;
        result.push(i);
        return result;
      },
      partnerSelected ? [partnerSelected] : [],
    );

    allocatedDebtOptionCached.current = result;

    return result;
  }, [listingAllowcatedDebt, partnerSelected, isGettedNewData]);

  const handleChangePartner = event => {
    const partnerSelected =
      allowcatedDebtOption?.find(i => i?.id === event.target.value) ?? {};
    setPartnerSelected(partnerSelected);
    onChange(partnerSelected);
  };

  useEffect(() => {
    if (!optionContainerRef.current) return;
    const { height } = optionContainerRef.current.getBoundingClientRect();
    setOptionContainerOverflowY(height >= 280 ? 'auto' : 'initial');
  }, [allowcatedDebtOption]);

  return (
    <Container style={{ ...styleSlots?.container }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <UserIcon style={{ marginRight: 8 }} />
        <Title style={{ fontWeight: 600, fontSize: 20, ...styleSlots?.title }}>
          {translation?.['b2b.txt.partner'] ?? ''}
        </Title>
      </div>
      <Row
        style={{ marginTop: 21, ...styleSlots?.optionContainer }}
        gutter={[16, 16]}
        {...propsSlot?.optionContainer}
      >
        <Col span={24} style={{ padding: `0 ${alignPadding}` }}>
          <StyledInput
            prefix={<SearchIcon style={{ marginRight: 4 }} />}
            placeholder={translation?.['b2b.txt.enterPartnerCodeOrName'] ?? ''}
            onChange={e => setSearchValue(e.target.value)}
            style={{ height: 44 }}
          />
        </Col>
        <Col span={24} style={{ padding: `0 ${alignPadding} 0 0` }}>
          {isSearching ? (
            <div style={{ padding: `0 0 0 ${alignPadding}` }}>
              <StyledInputSkeleton active />
              <StyledInputSkeleton
                active
                style={{ marginTop: 16, width: '40%' }}
              />
            </div>
          ) : allowcatedDebtOption?.length > 0 ? (
            <OptionContainer
              gutter={[0, 11]}
              ref={optionContainerRef}
              style={{ overflowY: optionContainerOverflowY }}
            >
              {allowcatedDebtOption?.map(partner => {
                const textDisplay = (() => {
                  if (!partner?.code) return partner?.name ?? '';
                  return `${partner.code} - ${partner.name}`;
                })();

                return (
                  <Col key={partner.id} span={24}>
                    <StyledCheckbox
                      antCheckboxWrapperCSS={{
                        width: '100%',
                        padding: `0 0 0 ${alignPadding}`,
                      }}
                      antCheckboxInner={{
                        borderColor: '#808080',
                      }}
                      value={partner.id}
                      checked={partnerSelected?.id === partner.id}
                      onChange={handleChangePartner}
                    >
                      <span style={{ fontWeight: 400 }}>{textDisplay}</span>
                    </StyledCheckbox>
                  </Col>
                );
              })}
            </OptionContainer>
          ) : (
            <div style={{ padding: `0 0 0 ${alignPadding}` }}>
              <span>
                {translation?.['b2b.txt.haveNoSuitablePartner'] ?? ''}
              </span>
            </div>
          )}
        </Col>
        <Col span={24} className="d-flex justify-content-center">
          <StyledButton
            disabled={!partnerSelected}
            textColor="#ffffff !important"
            height="40px"
            style={{ minWidth: 180, width: 'initial' }}
            disabledStyle={{
              backgroundColor: '#EDEDED !important',
            }}
            onClick={onProceedToBookTicket?.bind(null, partnerSelected?.id)}
          >
            {translation?.['b2b.txt.proceedToBook'] ?? ''}
          </StyledButton>
        </Col>
      </Row>
    </Container>
  );
};

export default Partner;

const Container = styled.div`
  background-color: #ffffff;
  border-radius: 20px;
`;

const Title = styled.p`
  font-size: 18px;
  font-weight: 600;
  color: '#000000';
  margin: 0;
`;

const StyledInput = styled(Input)`
  border-radius: 8px;
  padding: 13px;
  border-color: #dddddd !important;
  box-shadow: none !important;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  input::placeholder {
    color: #808080;
    opacity: 0.8;
  }
`;

const OptionContainer = styled(Row)`
  max-height: 285px;
  overflow-x: visible;
  word-break: break-word;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: none;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #ececec;
    border-radius: 10px;
  }
  &::-webkit-scrollbar {
    height: 6px;
  }
`;

const StyledInputSkeleton = styled(Skeleton.Input)`
  width: 100%;
  border-radius: 6px;
  .ant-skeleton-input {
    width: 100%;
    height: 21px;
  }
`;
